// IPQC: tách chỉ tiêu mẫu thành 4M (duyệt setup) và sản phẩm (ký mẫu), cột kiểm tra lặp lại
import { fourMOrder, isFourMCategory } from "@apv-erp/status";
import { normalizeChecklistItems, type IsoCriterionResult } from "./checklist";

/** Mẫu cũ chưa có nhóm 4M: thêm 4 dòng 4M mặc định (định tính) */
export function defaultFourMItems(): IsoCriterionResult[] {
  const base = (id: string, name: string, category: string, standard: string): IsoCriterionResult => ({
    id,
    name,
    category,
    standard,
    type: "QUALITATIVE",
    defectType: "MAJOR",
    required: true,
    stage: "SETUP",
    measuredValue: "",
    result: "PENDING",
    note: "",
  });
  return [
    base("ipqc_4m_man", "Thợ đứng máy", "Người", "Đúng thợ, đã đào tạo"),
    base("ipqc_4m_machine", "Máy, thông số", "Máy", "Máy sạch; thông số theo lệnh"),
    base("ipqc_4m_material", "Vật tư đúng lệnh", "Vật tư", "Đúng mã, lô; đã qua IQC"),
    base("ipqc_4m_method", "Lệnh & tài liệu", "Phương pháp", "Đúng lệnh, maquette, khuôn / kẽm"),
  ];
}

function sortFourM(items: IsoCriterionResult[]): IsoCriterionResult[] {
  return items
    .map((it, idx) => ({ it, idx }))
    .sort((a, b) => fourMOrder(a.it.category) - fourMOrder(b.it.category) || a.idx - b.idx)
    .map((x) => x.it);
}

/** Chia chỉ tiêu (đã chuẩn hoá) thành 4M và sản phẩm */
export function splitIpqcItems(items: IsoCriterionResult[]): { fourM: IsoCriterionResult[]; product: IsoCriterionResult[] } {
  const fourM = sortFourM(items.filter((it) => isFourMCategory(it.category)));
  const product = items.filter((it) => !isFourMCategory(it.category));
  return { fourM: fourM.length > 0 ? fourM : defaultFourMItems(), product };
}

/** Chỉ tiêu mẫu → checklist 4M + sản phẩm của phiếu mới (chưa chấm) */
export function buildIpqcChecklists(templateCriteria: any[]): { fourM: IsoCriterionResult[]; product: IsoCriterionResult[] } {
  return splitIpqcItems(normalizeChecklistItems(templateCriteria, [], { pendingByDefault: true }));
}

/** Cột của bảng kiểm tra lặp lại: chỉ tiêu được đánh dấu "lặp lại" */
export function repeatColumnsOf(...lists: IsoCriterionResult[][]): IsoCriterionResult[] {
  return lists.flat().filter((it) => it.repeat === true);
}

/** Thông tin một phiếu IPQC có theo luồng đợt 3 (có checklist 4M) không */
export function isIpqcNewFlow(record: any): boolean {
  return Array.isArray(record?.fourMChecklist) || (record?.fourMChecklist && typeof record.fourMChecklist === "object");
}

/** Nhãn thông số máy trong technicalParams của phiếu cũ (chỉ đọc) */
const LEGACY_PARAM_LABELS: Record<string, string> = {
  visualColorMatch: "Màu so mẫu",
  cmykDensity: "Mật độ K/C/M/Y",
  machineSpeed: "Tốc độ",
  colorTargetName: "Mã màu",
  labStdL: "L* chuẩn",
  labStdA: "a* chuẩn",
  labStdB: "b* chuẩn",
  labActL: "L* đo",
  labActA: "a* đo",
  labActB: "b* đo",
  deltaE: "ΔE",
  registrationOffset: "Chồng màu (mm)",
  viewingLight: "Đèn soi",
  rollerTemp: "Nhiệt độ lô",
  pressure: "Áp lực",
  dyneLevel: "Sức căng màng",
  glossUnit: "Độ bóng",
  viscosity: "Độ nhớt",
  uvEnergy: "Năng lượng UV",
  moldCode: "Mã khuôn",
  dieCutOffset: "Lệch bế (mm)",
  foilTemp: "Nhiệt độ ép kim",
  glueCode: "Loại keo",
  fiberTearPct: "Xé sợi (%)",
  gluerSpeed: "Tốc độ dán",
  cutSize: "Khổ cắt",
  diagonalDiff: "Lệch chéo (mm)",
  grain: "Sớ giấy",
};

const VISUAL_LABEL: Record<string, string> = { PASSED_VISUAL: "Đạt", LIMIT_SAMPLE: "Đạt giới hạn", FAILED_VISUAL: "Không đạt", FAILED: "Không đạt" };

export function legacyParamRows(tp: any): Array<[string, string]> {
  if (!tp || typeof tp !== "object") return [];
  return Object.entries(tp)
    .filter(([k, v]) => LEGACY_PARAM_LABELS[k] && v !== null && v !== undefined && v !== "")
    .map(([k, v]) => [LEGACY_PARAM_LABELS[k], k === "visualColorMatch" ? VISUAL_LABEL[String(v)] || String(v) : String(v)]);
}

